import { ENTITLEMENTS } from '../../application-core/constants';
import PropTypes from 'prop-types';
import React from 'react';
import GS from '../../grafsync/grafsync';
import { makeCancelable } from '../utils/utils';
import WebWorker from '../utils/workerSetup';
import Warning from '../Warning';
import TemplateSelector from './TemplateSelector';
import TSVbuilder from './TSVbuilder';
import Associations from '../Associations';
import XLSX from 'xlsx';

class GenerateTSV extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            isProcessing: false,
            isErrored: false,
            template: undefined,
            allowSave: true
        };
        this.cancellable = null;
    }

    doExport(exportName, resource, graph, format, template) {
        let properties = [...new Set(template.tsvColumnProperty)];
        if (this.props.options.sortProperty && !properties.includes(this.props.options.sortProperty)) {
            properties.push(this.props.options.sortProperty);
        }
        const langs = [...new Set([this.props.defaultLang, ...template.tsvColumnLanguage])];
        this.cancellable = makeCancelable(GS.export(exportName, resource, graph, format, langs, properties));
        return this.cancellable;
    }

    componentWillUnmount() {
        if (this.cancellable) this.cancellable.cancel();
    }

    onLoadAssociations = (associations) => this.setState({ associations });

    processTSV = (data, template, format) => {

        this.worker = new WebWorker(this.props.tsvFunction);

        this.worker.addEventListener('message',
            (tsv) => {
                const filename = this.props.resource.replace(/[^a-z0-9]/gi, '_').toLowerCase();
                let bookType = format;
                if (format === "xlsx") {
                    const ws = XLSX.utils.aoa_to_sheet(tsv.data);
                    const wb = XLSX.utils.book_new();
                    XLSX.utils.book_append_sheet(wb, ws, "Sheet");
                    XLSX.writeFile(wb, filename + '.' + format, {
                        bookType
                    });
                }
                else {
                    // We have to do it this way to stop SheetJS adding a BOM at start of file
                    // https://github.com/SheetJS/sheetjs/issues/2620
                    let noBOM = tsv.data;
                    if (format === "csv") {
                        const ws = XLSX.utils.aoa_to_sheet(tsv.data);
                        noBOM = XLSX.utils.sheet_to_csv(ws);
                    }
                    else if (format === "html") {
                        const ws = XLSX.utils.aoa_to_sheet(tsv.data);
                        noBOM = XLSX.utils.sheet_to_html(ws);
                    }
                    const url = window.URL.createObjectURL(new Blob([noBOM]));
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', filename + "." + format);
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(url);
                }
                if (this.cancellable && !this.cancellable.isCancelled()) this.setState({ isProcessing: false });
            },
            false);

        this.worker.onerror = (e) => {
            if (this.cancellable && !this.cancellable.isCancelled())
                this.setState({
                    isErrored: true,
                    isProcessing: false,
                    errorMessage: "An error occurred generating the TSV file."
                });
        };

        this.worker.postMessage({
            id: this.props.editedIRI || this.props.resource,
            data,
            mode: this.props.tsvMode,
            format,
            options: Object.assign({ associations: this.state.associations }, this.props.options, { defaultLang: this.props.defaultLang }),
            template: Object.assign({}, template)
        });
    };

    createTSV = async (template, format = "tsv") => {
        try {
            this.setState({ isProcessing: true });
            const { exportName, labelsExportName, resource, graph } = this.props;
            const referencesAsLabels = template.tsvTemplateOption
                && template.tsvTemplateOption.includes("https://grafsync.graphifi.com/schema/tsv/referencesAsLabels");
            const exportToUse = referencesAsLabels ? labelsExportName : exportName;
            const data = await this.doExport(exportToUse, resource, graph, "application/ld+json", template).promise;
            this.processTSV(data, template, format);
        }
        catch (e) {
            if (this.cancellable && !this.cancellable.isCancelled())
                this.setState({
                    isErrored: true,
                    isProcessing: false,
                    errorMessage: "Could not download data for export."
                });
        }
    };

    applyOptions = (options) => this.setState({ options });

    onExitWarning = () => this.setState({ isErrored: false });

    onSelect = (template, allowSave) => this.setState({ template, allowSave });

    discardTemplate = () => this.setState({ template: undefined });

    render() {

        const { options, defaultLang, graph, resource, ontologyProjectAssociation, examples, entitlements, templateTarget } = this.props;
        const { isErrored, isProcessing, errorMessage, template, allowSave } = this.state;
        const canCreateTemplate = entitlements.includes(ENTITLEMENTS.CREATE_TSV_TEMPLATE);

        const templateTargetOrSelf = templateTarget || resource;

        return (
            <div>
                {!template ?
                    <TemplateSelector
                        onSelect={this.onSelect}
                        graph={graph}
                        id={templateTargetOrSelf}
                        defaultLang={defaultLang} />
                    :
                    <Associations
                        graph={graph}
                        onLoad={this.onLoadAssociations}
                        ontologyProjectAssociation={ontologyProjectAssociation}
                        component={TSVbuilder}
                        templateTarget={templateTargetOrSelf}
                        id={resource}
                        isProcessing={isProcessing}
                        allowSave={canCreateTemplate && allowSave}
                        examples={examples}
                        onCreate={this.createTSV}
                        onDiscard={this.discardTemplate}
                        options={options}
                        defaultLang={defaultLang}
                        template={template} />
                }
                {isErrored && <Warning message={errorMessage} onExitWarning={this.onExitWarning} />}
            </div>
        );
    }
}

GenerateTSV.defaultProps = {
    options: {}
};

GenerateTSV.propTypes = {
    ontologyProjectAssociation: PropTypes.array,
    tsvFunction: PropTypes.func.isRequired,
    tsvMode: PropTypes.string.isRequired,
    templateTarget: PropTypes.string,
    options: PropTypes.object,
    resource: PropTypes.string.isRequired,
    labelsExportName: PropTypes.string,
    exportName: PropTypes.string.isRequired,
    graph: PropTypes.string.isRequired,
    examples: PropTypes.array,
    editedIRI: PropTypes.string,
    defaultLang: PropTypes.string.isRequired,
    classes: PropTypes.object
};

export default GenerateTSV;